import React, { useRef } from 'react';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import hexToRgba from '../../../../shared/utils/hexToRgba';

const Container = styled('div')`
  position: relative;
  background-color: ${(props) => (props.hasSessionBreakBackground ? '#F8F8F8' : 'transparent')};
`;

const ItemRow = styled('div')`
  display: flex;
  position: relative;
  padding-bottom: 8px;
  align-items: center;
  margin-left: ${(props) => (props.isWeekView ? '8px' : '0px')};
  margin-right: ${(props) => (props.isWeekView ? '8px' : '0px')};
`;

const LineContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-left: 5px;
  width: 100%;
  overflow-x: hidden;
`;

const Line = styled('div')`
  position: absolute;
  top: 0px;
  bottom: ${(props) => (props.doesGroupingLetterBelowItemMatchContent && !props.isDragging && !props.isHovered ? '0' : '8px')};
  width: 2px;
  background-color: ${(props) => props.color || '#E74C3C'};
`;

const TextColumn = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
  overflow: hidden;
  margin-left: 7px;
  position: relative;
`;

const SubTitle = styled('div')`
  overflow: hidden;
  font-size: 12px;
  color: #9E9E9E;
  font-family: 'Nunito Sans';
`;

const IconContainer = styled('div')`
  position: relative;
  background: ${(props) => props.background};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #EEEEEE;
  min-height: 25px;
  min-width: 25px;
  max-width: 25px;
  max-height: 25px;
  border: ${(props) => `1px solid ${props.borderColor}`};
`;

const ParentOrSwapIcon = styled('div')`
  position: absolute;
  top: -10px;
  left: -7px;
  height: 16px;
  width: 16px;
  background: #B5B5B5;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const ParentIconBorderBox = styled('div')`
  border: 1px solid white;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  width: 12px;
  text-align: center;
`;

const IconAndCheckBoxContainer = styled('div')`
display: flex;
height: 100%;
align-self: self-start;
gap: ${(props) => (props.isWeekView ? '0px' : '8px')};
margin-left: 2px;
.checkbox-input {
    visibility: ${(props) => !props.checkboxVisible && 'hidden'};
    appearance: none; /* Remove default checkbox styling */
    min-width: 18px;
    min-height: 18px;
    max-width: 18px;
    max-height: 18px;
    border: 2px solid ${(props) => (props.isChecked ? '#77A7FF' : '#E0E0E0')};
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    position: relative;
  }

  .checkbox-input:checked::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 5px;
    width: 5px;
    height: 10px;
    border: solid ${(props) => (props.isChecked ? '#77A7FF' : '#E0E0E0')};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: block;
  }
`;

const RightBoxPlaceholder = styled('div')`
  display: flex;
  visibility: hidden;
`;

const ClickContainer = styled('div')`
  padding-bottom: ${(props) => props.addPaddingBottom && '5px'};
  display: flex;
  overflow: hidden;
  transition: height 0.3s ease;
  justify-content: space-between;
  align-items: center;
  .rc-menu {
    padding: 10px;
  }
  padding-top: ${(props) => props.isSuperSet && '5px'};
  padding-bottom: ${(props) => (props.type === 'progression' || props.type === 'P') && props.isExpanded && '5px'}
`;

const ReadOnlyExerciseItem = ({
  content,
  exercises = [],
  index,
  hasSessionBreakBackground = false,
}) => {
  const exercisePickerTextRef = useRef(null);
  const itemRowRef = useRef(null);
  const textWrapperRef = useRef(null);
  const clickContainerRef = useRef(null);
  const selectedItem = useSelector((state) => (
    state.calendar.data.selectedItems[content.assignedId]
  ));

  const doesGroupingLetterAboveItemMatchContent = !!content.groupingLetter
    && index > 0
    && content.groupingLetter === exercises[index - 1]?.groupingLetter;

  const doesGroupingLetterBelowItemMatchContent = !!content.groupingLetter
    && index < exercises.length - 1
    && content.groupingLetter === exercises[index + 1]?.groupingLetter;

  const isChecked = !!selectedItem;

  const iconColorWriter = (icon) => {
    if (icon === 'question-mark-circle') {
      return '#9B59B6';
    }
    if (icon === 'science') {
      return '#9B59B6';
    }
    if (icon === 'warmup') {
      return '#E74C3C';
    }
    if (icon === 'lift') {
      return '#6C7A89';
    }
    if (icon === 'cardio') {
      return '#59ABE3';
    }
    if (icon === 'circuit') {
      return '#F89406';
    }
    if (icon === 'cooldown') {
      return '#1787FB';
    }
    if (icon === 'health' || icon === 'heart') {
      return '#E74C3C';
    }
    if (icon === 'note') {
      return '#F9BF3B';
    }
    if (icon === 'chart') {
      return '#FF00BF';
    }
    if (icon === 'tag2' || icon === 'tag') {
      return '#FF4500';
    }
    if (icon === 'page-break' || icon === 'break') {
      return 'white';
    }
    if (icon === 'rest' || icon === 'clock') {
      return '#6C7A89';
    }
    if (icon === 'video-add' || icon === 'video') {
      return '#6C7A89';
    }
    if (icon === 'plus') {
      return '#9E9E9E';
    }
    return '';
  };

  const iconBackgroundColor = ({ line }) => {
    const icon = content.groupingLetter ? 'groupingLetter' : content.icon;
    if (icon === 'question-mark-circle') {
      return 'rgba(155, 89, 182, 0.2)';
    }
    if (icon === 'science') {
      return 'rgba(155, 89, 182, 0.2)';
    }
    if (icon === 'warmup') {
      return 'rgba(231, 76, 60, 0.2)';
    }
    if (icon === 'lift') {
      return 'rgba(108, 122, 137, 0.2)';
    }
    if (icon === 'cardio') {
      return 'rgba(89, 171, 227, 0.2)';
    }
    if (icon === 'circuit') {
      return 'rgba(248, 148, 6, 0.2)';
    }
    if (icon === 'cooldown') {
      return 'rgba(23, 135, 251, 0.2)';
    }
    if (icon === 'health' || icon === 'heart') {
      return 'rgba(231, 76, 60, 0.2)';
    }
    if (icon === 'note') {
      return 'rgba(249, 191, 59, 0.2)';
    }
    if (icon === 'chart') {
      return 'rgba(255, 0, 191, 0.2)';
    }
    if (icon === 'tag2' || icon === 'tag') {
      return 'rgba(255, 69, 0, 0.2)';
    }
    if (icon === 'page-break' || icon === 'break') {
      if (line) {
        return 'rgba(108, 122, 137, 0.2)';
      }
      return '#34495e';
    }
    if (icon === 'rest' || icon === 'clock') {
      return 'rgba(108, 122, 137, 0.2)';
    }
    if (icon === 'video-add' || icon === 'video') {
      return 'rgba(108, 122, 137, 0.2)';
    }
    if (icon === 'plus') {
      return 'white';
    }
    if (icon === 'groupingLetter') {
      return hexToRgba(content?.groupingColorCode, 0.2);
    }
    return '';
  };

  const accessoryItem = (icon) => {
    if (icon === 'rest' || icon === 'clock') {
      return true;
    }
    if (icon === 'video-add' || icon === 'video') {
      return true;
    }
    if (icon === 'plus') {
      return true;
    }
    return false;
  };

  const decodeHtmlEntities = (str) => {
    if (!str) return str;
    return str
      .replace(/&apos;/g, '\'')
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  const getName = () => {
    if (content.type === 'video' && !content.title) {
      return 'Video';
    }
    if (content.type === 'session_break' && (!content.title || content.title === 'Session')) {
      return 'Session Break';
    }
    if (content.type === 'rest') {
      if (content.restTime) {
        return `Rest Time (${content?.restTime} sec)`;
      }
      return 'Rest Time';
    }
    return content.title;
  };

  return (
    <Container hasSessionBreakBackground={hasSessionBreakBackground}>
      <ItemRow
        ref={itemRowRef}
        style={{ opacity: 1 }}
        doesGroupingLetterAboveItemMatchContent={doesGroupingLetterAboveItemMatchContent}
        doesGroupingLetterBelowItemMatchContent={doesGroupingLetterBelowItemMatchContent}
      >
        <IconAndCheckBoxContainer
          isChecked={isChecked}
        >
          <IconContainer background={accessoryItem(content.icon) && !content.groupingLetter ? 'white' : iconBackgroundColor({ line: false })} borderColor={iconBackgroundColor({ line: false })}>
            {content.groupingLetter ? (
              <Text fontSize='13px' color={content.groupingColorCode}>{content.groupingLetter}</Text>
            ) : (
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon={content.icon === 'break' ? 'page-break' : content.icon}
                color={iconColorWriter(content.icon)}
              />
            )}
            {content.isInherited && (
            <ParentOrSwapIcon>
              <ParentIconBorderBox>
                <Text fontSize={10} style={{ marginTop: 3 }} color='white'>
                  P
                </Text>
              </ParentIconBorderBox>
            </ParentOrSwapIcon>
            )}
            {content?.swap && (
            <ParentOrSwapIcon>
              <IcomoonReact
                iconSet={iconSet}
                size={10}
                icon='swap'
                color='white'
              />
            </ParentOrSwapIcon>
            )}
          </IconContainer>
        </IconAndCheckBoxContainer>
        <LineContainer>
          <Line
            color={
            content.groupingColorCode
              ? content.groupingColorCode
              : iconBackgroundColor({ line: true })
            }
            doesGroupingLetterAboveItemMatchContent={doesGroupingLetterAboveItemMatchContent}
            doesGroupingLetterBelowItemMatchContent={doesGroupingLetterBelowItemMatchContent}
            isDragging={false}
            isHovered={false}
          />
          <ClickContainer
            isPreSave={content.preSave}
            ref={clickContainerRef}
            type={content.type}
          >
            <TextColumn ref={textWrapperRef}>
              {/* Invisible measurement span for exercise picker text */}
              <span
                ref={exercisePickerTextRef}
                style={{
                  whiteSpace: 'nowrap',
                  font: 'inherit', // IMPORTANT: match font exactly
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  fontSize: 13,
                }}
              >
                {getName()}
              </span>
            </TextColumn>
            <RightBoxPlaceholder />
          </ClickContainer>
          <SubTitle
            style={{
              marginLeft: 7,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginRight: 18,
              maxWidth: 300,
            }}
          >
            {content.additionalInfo && (content.type === 'lift' || content.type === 'L' || content.type === 'T' || content.type === 'tag') ? `${content.subTitle} - ${decodeHtmlEntities(content.additionalInfo)}` : content.subTitle }
          </SubTitle>
        </LineContainer>
      </ItemRow>
    </Container>
  );
};

ReadOnlyExerciseItem.propTypes = {
  content: PropTypes.instanceOf(Object).isRequired,
  exercises: PropTypes.instanceOf(Array),
  index: PropTypes.number.isRequired,
  hasSessionBreakBackground: PropTypes.bool,
};

ReadOnlyExerciseItem.defaultProps = {
  exercises: [],
  hasSessionBreakBackground: false,
};

export default ReadOnlyExerciseItem;
